import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { IPost } from 'src/types/hygraph'
import { HygraphTopics, TOPIC_MAP } from 'src/utils/hygraph'
import { RedirectUrls } from '../../types/types'
import Categories from './Categories'
import HelpArticleCard from './HelpArticleCard'
import { CATEGORIES, HelpGroupKey, groupHelpPosts } from './constants'

type IProps = {
  posts: IPost[]
  topic: HygraphTopics
}

/** Stable in-page anchors for the jump bar; language-independent on purpose. */
const GROUP_ANCHOR: Record<HelpGroupKey, string> = {
  start: 'empezar',
  payments: 'pagos',
  operate: 'operar',
  models: 'modelos',
  other: 'mas'
}

const Category = ({ posts, topic }: IProps) => {
  const { t } = useTranslation('common')
  const { path } = TOPIC_MAP[topic]
  const groups = groupHelpPosts(topic, posts)
  // Literal keys per branch — the typed t() rejects template-literal keys.
  const groupLabel = (key: HelpGroupKey) => {
    switch (key) {
      case 'start':
        return t('HELP.GROUP_START')
      case 'payments':
        return t('HELP.GROUP_PAYMENTS')
      case 'operate':
        return t('HELP.GROUP_OPERATE')
      case 'models':
        return t('HELP.GROUP_MODELS')
      default:
        return t('HELP.GROUP_OTHER')
    }
  }
  const { title, image, description } = CATEGORIES[topic] ?? {}

  return (
    <>
      {/* Editorial header */}
      <section className="bg-cream-300 px-5 pt-[calc(var(--nav-h)+40px)] pb-16 md:px-10 md:pt-[calc(var(--nav-h)+64px)] md:pb-20">
        <div className="mx-auto w-full max-w-7xl">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center gap-2 font-display text-[12px] tracking-[0.06em] text-ink-200 md:mb-10 md:text-[13px]">
            <Link
              href={RedirectUrls.Help}
              className="transition-colors hover:text-blue-500"
            >
              {t('HELP.BREADCRUMB_HOME')}
            </Link>
            <span aria-hidden="true" className="text-ink-200/50">
              /
            </span>
            <span className="text-ink-700">{title}</span>
          </nav>

          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
            {/* Left rail — eyebrow + H1 + description */}
            <div className="md:col-span-7">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {t('HELP.CATEGORY_EYEBROW')}
              </p>
              <h1 className="mt-4 font-display text-[40px] leading-[1.05] font-medium tracking-[-0.015em] text-ink-700 md:mt-5 md:text-[68px]">
                {title}
              </h1>
              {description && (
                <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-ink-200 md:mt-6 md:text-[17px]">
                  {description}
                </p>
              )}
            </div>

            {/* Right rail — image, hairline frame */}
            {image && (
              <div className="md:col-span-5">
                <div className="overflow-hidden border border-ink-100/15 bg-white-500">
                  <div className="relative aspect-[4/3] w-full">
                    <Image
                      src={image}
                      alt={title ?? ''}
                      fill
                      className="object-cover"
                      sizes="(max-width: 899px) 100vw, 480px"
                      priority
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Articles list — grouped by job-to-be-done (HELP_GROUPS); flat when a topic has no groups */}
      <section className="border-t border-ink-100/15 bg-white-500 px-5 py-20 md:px-10 md:py-28">
        <div className="mx-auto w-full max-w-7xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-5">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {t('HELP.CATEGORY_ARTICLES_EYEBROW')}
              </p>
              <h2 className="mt-3 font-display text-[26px] leading-[1.15] font-medium tracking-[-0.01em] text-ink-700 md:text-[40px]">
                {`${t('HELP.CATEGORY_ARTICLES_TITLE_PRIMARY')} `}
                <span className="font-display text-blue-500 italic">
                  {title}
                </span>
              </h2>
            </div>
            {groups.length > 1 && (
              <nav
                aria-label={t('HELP.JUMP_LABEL')}
                className="md:col-span-7 md:self-end"
              >
                <ul className="flex flex-wrap gap-x-6 gap-y-2">
                  {groups.map((group) => (
                    <li key={group.key}>
                      <a
                        href={`#${GROUP_ANCHOR[group.key]}`}
                        className="border-b border-ink-100/40 pb-0.5 font-display text-[15px] text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500"
                      >
                        {groupLabel(group.key)}
                        <span className="ml-1.5 text-ink-200">
                          {group.posts.length}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>

          {groups.map((group) => (
            <div
              key={group.key}
              id={GROUP_ANCHOR[group.key]}
              className="mt-12 scroll-mt-[calc(var(--nav-h)+24px)] md:mt-16"
            >
              {groups.length > 1 && (
                <h3 className="mb-2 font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[24px]">
                  {groupLabel(group.key)}
                </h3>
              )}
              <ol className="grid grid-cols-1 gap-0 md:grid-cols-2">
                {group.posts.map(({ title: postTitle, slug, description }) => (
                  <HelpArticleCard
                    title={postTitle}
                    href={`${path}/${slug}`}
                    description={description ?? ''}
                    key={slug}
                  />
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>

      {/* Other categories */}
      <Categories
        variant="default"
        excludeTopic={topic}
        eyebrowKey="CATEGORY_OTHER_EYEBROW"
        titlePrimaryKey="CATEGORY_OTHER_TITLE_PRIMARY"
        titleItalicKey="CATEGORY_OTHER_TITLE_ITALIC"
        showSubtitle={false}
      />
    </>
  )
}

export default Category
